import { Outlet, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent } from '../components/ui/sheet';
import { AppSidebar } from '../components/layout/AppSidebar';
import { FullPageSpinner } from '../components/layout/FullPageSpinner';
import { TopBar } from '../components/layout/TopBar';
import { sessionQueryOptions, useSignOutMutation } from '../features/auth/auth.queries';
import type { SessionUser } from '../features/auth/auth.queries';
import { strings } from '../features/auth/auth.strings';

export const Route = createFileRoute('/_authed')({
  beforeLoad: async ({ context, location }) => {
    const data = await context.queryClient.ensureQueryData(sessionQueryOptions);
    if (!data?.user) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    return { session: data };
  },
  pendingComponent: FullPageSpinner,
  component: AuthedLayout,
});

export function computeInitials(input: string): string {
  const parts = input.trim().split(/\s+/);
  const first = parts[0] ?? '';
  const second = parts[1] ?? '';
  if (parts.length >= 2 && first && second) {
    return ((first[0] ?? '') + (second[0] ?? '')).toUpperCase();
  }
  return input.slice(0, 2).toUpperCase();
}

function UserMenu({ user }: { user: SessionUser | undefined }) {
  const navigate = useNavigate();
  const signOutMutation = useSignOutMutation();

  if (!user) return null;

  const initials = computeInitials(user.name ?? user.email);

  async function handleSignOut() {
    await signOutMutation.mutateAsync();
    await navigate({ to: '/login' });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex size-9 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary hover:bg-primary/15"
        >
          {initials}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="text-sm font-medium">{user.name ?? 'Sin nombre'}</span>
          <span className="text-xs text-muted-foreground">{user.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled>{strings.userMenuAccount}</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void handleSignOut()}>
          {strings.userMenuSignOut}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ShellTopBar({ onMenuClick }: { onMenuClick?: () => void }) {
  const { data } = useQuery(sessionQueryOptions);
  const user = data?.user;

  return <TopBar onMenuClick={onMenuClick} actions={<UserMenu user={user} />} />;
}

function AuthedLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* TopBar — sticky header */}
      <ShellTopBar onMenuClick={() => setIsMobileMenuOpen(true)} />

      {/* Main body: sidebar + content */}
      <div className="flex-1 flex min-w-0">
        {/* Desktop Sidebar (hidden on mobile) */}
        <AppSidebar className="hidden lg:flex w-[var(--sidebar-width)] shrink-0 border-r border-sidebar-border bg-sidebar h-[calc(100vh-var(--header-height))] sticky top-[var(--header-height)]" />

        {/* Mobile Sidebar Sheet */}
        <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
          <SheetContent side="left" className="w-[280px] p-0 bg-sidebar border-sidebar-border">
            <AppSidebar
              className="h-full"
              onNavItemClick={() => setIsMobileMenuOpen(false)}
            />
          </SheetContent>
        </Sheet>

        {/* Main Content Area */}
        <main className="flex-1 min-w-0 overflow-y-auto flex flex-col">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
